import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';
import { signInWithPopup, signOut } from 'firebase/auth';
import { WEDDING_CONFIG } from '../config/weddingConfig';
import { parseCSVToGuests, importGuestsToFirestore } from '../utils/importGuestsFromCSV';

// Define authorized admin emails
const ADMIN_EMAILS = ['jamesandbecca27@gmail.com', 'footyjamez15@gmail.com', 'beccakpage@gmail.com'];

function Admin() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(true);
  const [importMessage, setImportMessage] = useState('');
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [addGuestForm, setAddGuestForm] = useState({ firstName: '', lastName: '', invitedAs: 'allDay' });
  const [addGuestMessage, setAddGuestMessage] = useState('');
  const [selectedGuest, setSelectedGuest] = useState(null);
  
  // Filter and sort state
  const [filterRsvpStatus, setFilterRsvpStatus] = useState('all'); // all, rsvpd, not-rsvpd
  const [filterAttendance, setFilterAttendance] = useState('all'); // all, attending, not-attending
  const [filterInviteType, setFilterInviteType] = useState('all'); // all, allDay, evening
  const [sortBy, setSortBy] = useState('name'); // name, rsvpDate, inviteType
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
  const [searchName, setSearchName] = useState('');
  const [filterHasComment, setFilterHasComment] = useState(false);
  const [filterHasMusicRequest, setFilterHasMusicRequest] = useState(false);
  const [filterHasPlusOne, setFilterHasPlusOne] = useState(false);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Real-time listener for guests - automatically loads and updates
  useEffect(() => {
    if (!user || !ADMIN_EMAILS.includes(user.email)) {
      return;
    }

    setLoadingGuests(true);
    
    const unsubscribe = onSnapshot(
      collection(db, 'guests'),
      (snapshot) => {
        const guestData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setGuests(guestData);
        setLoadingGuests(false);
      },
      (error) => {
        console.error('Error fetching guests:', error);
        setLoadingGuests(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Removed RSVP listener - using Guest Management section only

  const handleGoogleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Sign in error:', error);
      alert('Failed to sign in. Please try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setGuests([]);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleCSVImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportMessage('Reading CSV file...');
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const csvText = e.target.result;
        const guests = parseCSVToGuests(csvText);
        
        if (guests.length === 0) {
          setImportMessage('Error: No valid guests found in CSV file.');
          return;
        }

        setImportMessage(`Found ${guests.length} guests. Importing...`);
        
        const result = await importGuestsToFirestore(guests);
        if (result.success) {
          setImportMessage(`Import complete! Added: ${result.added}, Updated: ${result.updated}, Skipped: ${result.skipped}`);
          // No need to manually refresh - real-time listener will update automatically
        } else {
          setImportMessage(`Error: ${result.error}`);
        }
      } catch (error) {
        console.error('Error importing CSV:', error);
        setImportMessage(`Error: ${error.message}`);
      }
    };
    
    reader.onerror = () => {
      setImportMessage('Error reading file.');
    };
    
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  const handleAddGuest = async () => {
    const { firstName, lastName, invitedAs } = addGuestForm;
    if (!firstName.trim() || !lastName.trim()) {
      setAddGuestMessage('Please enter both first and last name.');
      return;
    }

    const guestCode = `${firstName.toLowerCase().trim()}${lastName.toLowerCase().trim()}`;
    setAddGuestMessage('Adding guest...');

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setAddGuestMessage('You must be signed in.');
        return;
      }
      await currentUser.getIdToken(true);

      const guestRef = doc(db, 'guests', guestCode);
      const guestSnap = await getDoc(guestRef);

      // If the base code exists, find the next available numeric suffix
      let finalGuestCode = guestCode;
      if (guestSnap.exists()) {
        let suffix = 2;
        while (true) {
          const candidateCode = `${guestCode}${suffix}`;
          const candidateSnap = await getDoc(doc(db, 'guests', candidateCode));
          if (!candidateSnap.exists()) {
            finalGuestCode = candidateCode;
            break;
          }
          suffix++;
        }
      }

      await setDoc(doc(db, 'guests', finalGuestCode), {
        guestCode: finalGuestCode,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        invitedAs,
        email: '',
        phone: '',
        hasRSVPd: false,
        rsvpStatus: '',
        rsvpType: '',
        foodChoice: '',
        songRequest: '',
        updatedAt: null,
        createdAt: new Date()
      });

      setAddGuestMessage(
        finalGuestCode !== guestCode
          ? `Successfully added ${firstName.trim()} ${lastName.trim()}! (duplicate name — assigned code "${finalGuestCode}")`
          : `Successfully added ${firstName.trim()} ${lastName.trim()}!`
      );
      setAddGuestForm({ firstName: '', lastName: '', invitedAs: 'allDay' });
      setTimeout(() => {
        setShowAddGuest(false);
        setAddGuestMessage('');
      }, 1500);
    } catch (error) {
      console.error('Error adding guest:', error);
      setAddGuestMessage(`Error: ${error.message}`);
    }
  };

  const exportToCSV = () => {
    // Export guests who have RSVP'd
    const rsvpdGuests = guests.filter(g => g.hasRSVPd);
    
    if (rsvpdGuests.length === 0) {
      alert('No RSVP data to export');
      return;
    }

    // Create CSV headers
    const headers = ['Name', 'Email', 'Phone', 'Attending', 'RSVP Type', 'Invited As', 'Food Choice', 'Song Request', 'Has +1', '+1 Name', '+1 Email', '+1 Phone', 'Submitted At'];
    
    // Create CSV data
    const csvData = rsvpdGuests.map(guest => [
      `${guest.firstName} ${guest.lastName}`,
      guest.email || '',
      guest.phone || '',
      guest.rsvpStatus === 'attending' ? 'Yes' : 'No',
      guest.rsvpType || '',
      guest.invitedAs || '',
      guest.foodChoice || '',
      guest.songRequest || '',
      guest.hasPlusOne ? 'Yes' : 'No',
      guest.hasPlusOne ? `${guest.plusOneFirstName} ${guest.plusOneLastName}` : '',
      guest.plusOneEmail || '',
      guest.plusOnePhone || '',
      guest.updatedAt?.toDate?.().toLocaleString() || ''
    ]);

    // Combine headers and data
    const allData = [headers, ...csvData];
    
    // Convert to CSV string
    const csvContent = allData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `wedding-rsvps-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadCSVTemplate = () => {
    // CSV template content
    const templateContent = `FirstName,LastName,InvitedAs,Email,Phone
James,McGovern,allDay,james@example.com,07123456789
John,Smith,allDay,john@example.com,07987654321
Jane,Doe,evening,jane@example.com,07111222333`;

    // Create and download file
    const blob = new Blob([templateContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'guest-import-template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#7d688f'}}>
        <div className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="relative z-10 pb-8 bg-transparent sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32">
              <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
                <div className="flex items-center justify-center">
                  <div className="text-xl" style={{color: '#a5bc8a'}}>Loading...</div>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not signed in
  if (!user) {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#7d688f'}}>
        <div className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="relative z-10 pb-8 bg-transparent sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32">
              <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
                <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
            <h1 className="text-3xl font-bold text-center mb-8" style={{ color: WEDDING_CONFIG.COLORS.PRIMARY }}>
              Admin Dashboard
            </h1>
            <div className="text-center">
              <p className="text-gray-600 mb-6">
                Please sign in with your authorized Google account to access the admin dashboard.
              </p>
              <button
                onClick={handleGoogleSignIn}
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center mx-auto"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>
            </div>
          </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if user is authorized
  if (!ADMIN_EMAILS.includes(user.email)) {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#7d688f'}}>
        <div className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="relative z-10 pb-8 bg-transparent sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32">
              <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
                <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
                  <h1 className="text-3xl font-bold text-center mb-8" style={{ color: WEDDING_CONFIG.COLORS.PRIMARY }}>
                    Access Denied
                  </h1>
                  <div className="text-center">
                    <p className="text-gray-600 mb-6">
                      Your email ({user.email}) is not authorized to access this admin dashboard.
                    </p>
                    <button
                      onClick={handleSignOut}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Filter and sort guests
  const getFilteredAndSortedGuests = () => {
    let filtered = [...guests];
    
    // Apply name search
    if (searchName.trim()) {
      const search = searchName.toLowerCase().trim();
      filtered = filtered.filter(g => 
        `${g.firstName} ${g.lastName}`.toLowerCase().includes(search)
      );
    }
    
    // Apply RSVP status filter
    if (filterRsvpStatus === 'rsvpd') {
      filtered = filtered.filter(g => g.hasRSVPd === true);
    } else if (filterRsvpStatus === 'not-rsvpd') {
      filtered = filtered.filter(g => g.hasRSVPd === false);
    }
    
    // Apply attendance filter
    if (filterAttendance === 'attending') {
      filtered = filtered.filter(g => g.rsvpStatus === 'attending');
    } else if (filterAttendance === 'not-attending') {
      filtered = filtered.filter(g => g.rsvpStatus === 'not-attending');
    }
    
    // Apply invite type filter
    if (filterInviteType !== 'all') {
      filtered = filtered.filter(g => g.invitedAs === filterInviteType);
    }

    if (filterHasComment) {
      filtered = filtered.filter(g => g.message && g.message.trim().length > 0);
    }
    if (filterHasMusicRequest) {
      filtered = filtered.filter(g => g.songRequest && g.songRequest.trim().length > 0);
    }
    if (filterHasPlusOne) {
      filtered = filtered.filter(g => g.hasPlusOne === true);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        case 'rsvpDate':
          const dateA = a.updatedAt?.toDate?.() || new Date(0);
          const dateB = b.updatedAt?.toDate?.() || new Date(0);
          comparison = dateA - dateB;
          break;
        case 'inviteType':
          comparison = (a.invitedAs || '').localeCompare(b.invitedAs || '');
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  };
  
  const filteredGuests = getFilteredAndSortedGuests();

  // Authorized admin view
  return (
    <div className="min-h-screen" style={{backgroundColor: '#7d688f'}}>
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 bg-transparent sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              {/* Header */}
              <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold" style={{ color: WEDDING_CONFIG.COLORS.PRIMARY }}>
              Admin Dashboard
            </h1>
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Welcome, {user.displayName}</span>
              <button
                onClick={handleSignOut}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Guest Management */}
        <div className="bg-white rounded-lg shadow-lg p-6 mt-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold" style={{ color: WEDDING_CONFIG.COLORS.SECONDARY }}>
              Guest Management {loadingGuests && <span className="text-sm font-normal text-gray-500">(Loading...)</span>}
            </h2>
            <div className="flex space-x-3">
              <button
                onClick={exportToCSV}
                disabled={guests.filter(g => g.hasRSVPd).length === 0}
                className="bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export RSVPs CSV
              </button>
              <button
                onClick={downloadCSVTemplate}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Template
              </button>
              <label className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 cursor-pointer flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Import CSV
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCSVImport}
                  className="hidden"
                />
              </label>
              <button
                onClick={() => { setShowAddGuest(true); setAddGuestMessage(''); }}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Guest
              </button>
            </div>
          </div>

          {importMessage && (
            <div className={`mb-4 p-3 rounded-md ${importMessage.includes('Error') ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
              {importMessage}
            </div>
          )}


          {/* Filter and Sort Controls */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            {/* Search by Name */}
            <div className="mb-4">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="Search by name..."
                  className="w-full pl-10 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                {searchName && (
                  <button
                    onClick={() => setSearchName('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* RSVP Status Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">RSVP Status</label>
                <select
                  value={filterRsvpStatus}
                  onChange={(e) => setFilterRsvpStatus(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">All Guests</option>
                  <option value="rsvpd">RSVP'd</option>
                  <option value="not-rsvpd">Not RSVP'd</option>
                </select>
              </div>

              {/* Attendance Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Attendance</label>
                <select
                  value={filterAttendance}
                  onChange={(e) => setFilterAttendance(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">All</option>
                  <option value="attending">Attending</option>
                  <option value="not-attending">Not Attending</option>
                </select>
              </div>

              {/* Invite Type Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Invite Type</label>
                <select
                  value={filterInviteType}
                  onChange={(e) => setFilterInviteType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">All</option>
                  <option value="allDay">All Day</option>
                  <option value="evening">Evening</option>
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Sort By</label>
                <div className="flex gap-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="name">Name</option>
                    <option value="rsvpDate">RSVP Date</option>
                    <option value="inviteType">Invite Type</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {sortOrder === 'asc' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                      )}
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Extra filters */}
            <div className="mt-4 flex flex-wrap gap-3">
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={filterHasComment}
                  onChange={(e) => setFilterHasComment(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Has comment / note
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={filterHasMusicRequest}
                  onChange={(e) => setFilterHasMusicRequest(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Has music request
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={filterHasPlusOne}
                  onChange={(e) => setFilterHasPlusOne(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Has +1
              </label>
            </div>
            
            {/* Active filters indicator */}
            {(filterRsvpStatus !== 'all' || filterAttendance !== 'all' || filterInviteType !== 'all' || searchName.trim() || filterHasComment || filterHasMusicRequest || filterHasPlusOne) && (
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-600">
                <span className="font-semibold">Showing {filteredGuests.length} of {guests.length} guests</span>
                <button
                  onClick={() => {
                    setFilterRsvpStatus('all');
                    setFilterAttendance('all');
                    setFilterInviteType('all');
                    setSearchName('');
                    setFilterHasComment(false);
                    setFilterHasMusicRequest(false);
                    setFilterHasPlusOne(false);
                  }}
                  className="text-purple-600 hover:text-purple-800 underline"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {/* Guest Stats */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-indigo-100 p-4 rounded-lg">
              <h3 className="font-semibold text-indigo-800">Total Guests</h3>
              <p className="text-2xl font-bold text-indigo-600">{guests.length}</p>
            </div>
            <div className="bg-green-100 p-4 rounded-lg">
              <h3 className="font-semibold text-green-800">All Day Guests</h3>
              <p className="text-2xl font-bold text-green-600">
                {guests.filter(g => g.invitedAs === 'allDay').length}
              </p>
            </div>
            <div className="bg-blue-100 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800">Evening Guests</h3>
              <p className="text-2xl font-bold text-blue-600">
                {guests.filter(g => g.invitedAs === 'evening').length}
              </p>
            </div>
            <div className="bg-yellow-100 p-4 rounded-lg">
              <h3 className="font-semibold text-yellow-800">RSVP'd</h3>
              <p className="text-2xl font-bold text-yellow-600">
                {guests.filter(g => g.hasRSVPd).length}
              </p>
            </div>
            <div className="bg-purple-100 p-4 rounded-lg">
              <h3 className="font-semibold text-purple-800">+1 Guests</h3>
              <p className="text-2xl font-bold text-purple-600">
                {guests.filter(g => g.hasPlusOne).length}
              </p>
            </div>
          </div>

          {/* Guest List */}
          {loadingGuests ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="text-gray-500 mt-2">Loading guests...</p>
            </div>
          ) : guests.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No guest data found. Data updates automatically.</p>
            </div>
          ) : filteredGuests.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No guests match the current filters.</p>
              <button
                onClick={() => {
                  setFilterRsvpStatus('all');
                  setFilterAttendance('all');
                  setFilterInviteType('all');
                  setSearchName('');
                  setFilterHasComment(false);
                  setFilterHasMusicRequest(false);
                  setFilterHasPlusOne(false);
                }}
                className="mt-2 text-purple-600 hover:text-purple-800 underline text-sm"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-auto text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    <th className="px-3 py-3 text-left">Name</th>
                    <th className="px-3 py-3 text-left">Type</th>
                    <th className="px-3 py-3 text-left">RSVP'd</th>
                    <th className="px-3 py-3 text-left">Response</th>
                    <th className="px-3 py-3 text-left">Food</th>
                    <th className="px-3 py-3 text-left">Email</th>
                    <th className="px-3 py-3 text-left">+1</th>
                    <th className="px-3 py-3 text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGuests.map((guest, index) => (
                    <tr
                      key={guest.id}
                      onClick={() => setSelectedGuest(guest)}
                      className={`cursor-pointer hover:bg-indigo-50 transition-colors duration-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="px-3 py-3 font-semibold text-gray-800">
                        <div className="flex items-center gap-2">
                          <span>{guest.firstName} {guest.lastName}</span>
                          <div className="flex gap-1">
                            {guest.songRequest && (
                              <span title={`Song: ${guest.songRequest}`} className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 text-indigo-600">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z"/>
                                </svg>
                              </span>
                            )}
                            {guest.message && (
                              <span title={guest.message} className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-600">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/>
                                </svg>
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          guest.invitedAs === 'allDay' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {guest.invitedAs === 'allDay' ? 'All Day' : 'Evening'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          guest.hasRSVPd ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {guest.hasRSVPd ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        {guest.rsvpStatus ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            guest.rsvpStatus === 'attending' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {guest.rsvpStatus === 'attending'
                              ? guest.rsvpType === 'allDay' ? 'All Day' : 'Evening'
                              : 'Not Attending'}
                          </span>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-700">{guest.foodChoice || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-3 text-sm text-gray-700">{guest.email || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-3">
                        {guest.hasPlusOne
                          ? <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Yes</span>
                          : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-3 text-gray-400 text-xs">View ›</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
            </main>
          </div>
        </div>
      </div>

      {/* Guest Detail Modal */}
      {selectedGuest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedGuest(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-screen overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex justify-between items-start p-6 border-b border-gray-100">
              <div>
                <h3 className="text-xl font-bold text-gray-800">{selectedGuest.firstName} {selectedGuest.lastName}</h3>
                <div className="flex gap-2 mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedGuest.invitedAs === 'allDay' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                    {selectedGuest.invitedAs === 'allDay' ? 'All Day Guest' : 'Evening Guest'}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedGuest.hasRSVPd ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                    {selectedGuest.hasRSVPd ? 'RSVP\'d' : 'Not yet RSVP\'d'}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelectedGuest(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-4">&times;</button>
            </div>

            <div className="p-6 space-y-5">
              {/* RSVP Response */}
              {selectedGuest.hasRSVPd && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">RSVP Response</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Status</p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedGuest.rsvpStatus === 'attending' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {selectedGuest.rsvpStatus === 'attending' ? 'Attending' : 'Not Attending'}
                      </span>
                    </div>
                    {selectedGuest.rsvpStatus === 'attending' && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Attending</p>
                        <p className="text-sm font-medium text-gray-800">{selectedGuest.rsvpType === 'allDay' ? 'All Day (Ceremony & Reception)' : 'Evening Reception Only'}</p>
                      </div>
                    )}
                    {selectedGuest.foodChoice && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Food Choice</p>
                        <p className="text-sm font-medium text-gray-800">{selectedGuest.foodChoice}</p>
                      </div>
                    )}
                    {selectedGuest.songRequest && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Song Request</p>
                        <p className="text-sm font-medium text-gray-800">{selectedGuest.songRequest}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Details */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Contact Details</h4>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b border-gray-50">
                    <span className="text-xs text-gray-500">Email</span>
                    <span className="text-sm text-gray-800 text-right break-all ml-4">{selectedGuest.email || '—'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-50">
                    <span className="text-xs text-gray-500">Phone</span>
                    <span className="text-sm text-gray-800">{selectedGuest.phone || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Plus One */}
              {selectedGuest.hasPlusOne && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Plus One</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-xs text-gray-500">Name</span>
                      <span className="text-sm font-medium text-gray-800">{selectedGuest.plusOneFirstName} {selectedGuest.plusOneLastName}</span>
                    </div>
                    {selectedGuest.plusOneEmail && (
                      <div className="flex justify-between py-2 border-b border-gray-50">
                        <span className="text-xs text-gray-500">Email</span>
                        <span className="text-sm text-gray-800 break-all ml-4">{selectedGuest.plusOneEmail}</span>
                      </div>
                    )}
                    {selectedGuest.plusOnePhone && (
                      <div className="flex justify-between py-2 border-b border-gray-50">
                        <span className="text-xs text-gray-500">Phone</span>
                        <span className="text-sm text-gray-800">{selectedGuest.plusOnePhone}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Message */}
              {selectedGuest.message && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Message</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{selectedGuest.message}</p>
                  </div>
                </div>
              )}

              {/* Meta */}
              {selectedGuest.updatedAt && (
                <p className="text-xs text-gray-400 text-right">
                  Last updated: {selectedGuest.updatedAt?.toDate?.().toLocaleString() || '—'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Guest Modal */}
      {showAddGuest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAddGuest(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Add New Guest</h3>
              <button onClick={() => setShowAddGuest(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={addGuestForm.firstName}
                  onChange={(e) => setAddGuestForm(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="First name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={addGuestForm.lastName}
                  onChange={(e) => setAddGuestForm(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Last name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invited As *</label>
                <select
                  value={addGuestForm.invitedAs}
                  onChange={(e) => setAddGuestForm(prev => ({ ...prev, invitedAs: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="allDay">All Day</option>
                  <option value="evening">Evening</option>
                </select>
              </div>

              {addGuestMessage && (
                <div className={`p-3 rounded-md text-sm ${addGuestMessage.includes('Error') || addGuestMessage.includes('already exists') ? 'bg-red-100 text-red-800' : addGuestMessage.includes('Successfully') ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                  {addGuestMessage}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowAddGuest(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddGuest}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors duration-200"
                >
                  Add Guest
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
